import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Template1 } from '@/components/ResumeTemplates/Template1';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, FileText } from 'lucide-react';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

export default function PublicResumeViewer() {
  const { resumeId } = useParams<{ resumeId: string }>();
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumeTitle, setResumeTitle] = useState<string>('');

  useEffect(() => {
    if (resumeId) {
      loadPublicResume(resumeId);
    }
  }, [resumeId]);

  const loadPublicResume = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch resume data from public API (no auth required)
      const { data, error } = await supabase
        .from('resumes')
        .select('id, title, parsed_resume_data')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching resume:', error);
        setError('Resume not found or not available for public viewing.');
        return;
      }

      if (!data.parsed_resume_data) {
        setError('Resume data is not available for viewing.');
        return;
      }

      setResumeTitle(data.title);
      setResumeData(data.parsed_resume_data as unknown as ResumeData);

    } catch (err) {
      console.error('Error loading public resume:', err);
      setError('Failed to load resume. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading resume...</p>
        </div>
      </div>
    );
  }

  if (error || !resumeData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Resume Not Available</h2>
            <p className="text-muted-foreground">
              {error || 'The requested resume could not be found or is not available for public viewing.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{resumeTitle}</h1>
              <p className="text-sm text-gray-600">Public Resume View</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resume Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <Template1 data={resumeData} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-gray-50 py-4 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            This resume is publicly viewable. Powered by TailorWiz.
          </p>
        </div>
      </div>
    </div>
  );
}